import styled, { keyframes } from 'styled-components';
import {
  colors,
  spacing,
  componentSpacing,
  mixins,
  layout,
  dimensions,
  fontSizes,
  fontWeights,
} from './design-system';
import { tokens } from 'design-tokens/warwick/tokens';
import {
  ContentBody,
  OuterContentContainer as BaseOuterContentContainer,
  ContentContainer as BaseContentContainer,
} from 'design-tokens/warwick/common';

const { space } = tokens.primitive;

/**
 * TV-Optimized Warwick Template Styles
 * - No backdrop-filter or heavy blur effects
 * - No box-shadow
 * - Simple transforms and opacity animations only
 * - Hardware acceleration with translate3d
 */

export const Container = styled.div`
  height: 100vh;
  width: 100vw;
  position: relative;
  overflow: hidden;
  ${mixins.flexColumn}
  box-sizing: border-box;
  background-color: ${tokens.primitive.color.whiteAlpha10};
`;

// Main Layout
export const MainLayout = styled.div`
  ${mixins.flexColumn}
  position: relative;
  flex: 1;
  margin-top: ${space[60]};
`;

// Carousel Container - Replaces MainContent and ServiceCardsGrid
export const CarouselContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-width: 0;
  flex: 1;
  padding: 40px ${tokens.primitive.space[186]};
`;

export const ServiceCardWrapper = styled.div`
  min-height: ${tokens.semantic.size.menuItem.minHeight};
  height: 100%;
  width: ${tokens.semantic.size.menuItem.width};
  flex-shrink: 0;
  padding: 0;
  position: relative;
  z-index: ${(props) => (props.active ? 10 : 1)};
  box-sizing: border-box;
`;

export const OuterContentContainer = styled.div`
  ${mixins.flexColumn}
  padding: ${spacing['5xl']};
  background-color: ${colors.surface.container};
  flex: 1;
`;

export const ContentContainer = styled.div`
  ${mixins.flexColumn}
  padding: ${spacing['2xl']} ${spacing['4xl']};
  background-color: ${colors.surface.content};
  flex: 1;
  border-radius: ${spacing.lg};
  border-color: ${colors.border.accent};

  > * + * {
    margin-top: ${spacing['2xl']};
  }
`;

export const ContentTopRow = styled.div`
  width: 100%;
  height: 12%;
  border-bottom: 1px solid ${colors.text.accent}; /* Golden color #C4AA6F */
  ${mixins.flexColumn}
  justify-content: center; /* Vertically center the content */
`;

export const ContentMiddleRow = styled.div`
  width: 100%;
  flex: 1; /* Takes remaining space after top (12%) and bottom (22%) */
`;

export const ContentBottomRow = styled.div`
  width: 100%;
  height: 22%;
`;

export const ContentStepContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center; /* Center aligned */
  align-items: center;
  width: 100%;
  height: 100%;
`;

// Middle Row Components
export const ContentMiddleRowContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;

  align-items: center;
  justify-content: center;

  > * + * {
    margin-inline-start: ${spacing.lg};
  }
`;

export const LeftColumn = styled.div`
  width: 40%;
  height: 100%;
  border-radius: ${spacing.base}; /* 1rem = 16px - exact match */
  padding: ${spacing.card.padding}; /* 16px top/bottom, 8px left/right */
  overflow: hidden;
  background-color: ${colors.surface.card};
`;

export const RightColumn = styled.div`
  width: 60%;
  height: 100%;
  border-radius: ${spacing.base}; /* 1rem = 16px - exact match */
  padding: ${spacing.card.padding}; /* 16px top/bottom, 8px left/right */
  ${mixins.flexColumn}

  overflow-y: auto; /* Scrollable */
  background-color: ${colors.surface.card};

  > * + * {
    margin-top: ${spacing.lg};
  }
`;

export const ItemTitle = styled.h2`
  font-size: ${fontSizes.bodySmall}; /* 1.25rem = 20px - closest to 22px */
  font-weight: ${fontWeights.bold}; /* 800 - closest to 700 */
  color: ${colors.text.accent}; /* #C4AA6F - goldish color */
  line-height: 1.2;
`;

export const ItemDescription = styled.p`
  font-size: ${fontSizes.caption}; /* 1.125rem = 18px - exact match */
  font-weight: ${fontWeights.regular}; /* 400 - exact match */
  color: ${colors.text.primary}; /* #403A60 - purplish color */
  line-height: 1.4;
  flex: 1;
`;

export const ErrorContainer = styled.div`
  ${mixins.flexCenter}
  width: 100%;
  height: 100%;
  text-align: center;
`;

export const ErrorText = styled.p`
  font-size: ${fontSizes.body}; /* 1.5rem = 24px */
  font-weight: ${fontWeights.bold}; /* 800 */
  color: #e60e0e; /* Red color for error */
  margin: 0;
`;

export const ContentStepItem = styled.button`
  margin: ${spacing.sm}; /* 8px margin creates gap between items */
  padding: 0; /* No padding as requested */
  width: 12.5rem; /* 200px */
  height: 100%; /* Take full height of container */
  border-radius: 1.125rem; /* 18px */
  background-color: ${colors.surface.primary}; /* #403A60 purplish */
  color: ${colors.text.inverse}; /* white text */
  border: 2px solid ${(props) => (props.active ? colors.text.accent : 'transparent')}; /* Golden border when active */
  font-size: ${fontSizes.bodySmall}; /* 1.25rem = 20px - closest to 16px */
  font-weight: ${fontWeights.medium}; /* 500 - exact match */
  cursor: pointer;

  /* Remove default button styles */
  outline: none;

  /* Focus ring for accessibility */
  &:focus {
    outline: 2px solid ${colors.text.accent};
    outline-offset: 2px;
  }
`;

// Top Row Content Components
export const TopRowContent = styled.div`
  ${mixins.flexColumn}
  align-items: flex-start;
`;

export const ContentHeading = styled.h1`
  font-size: ${fontSizes.title}; /* 1.6875rem = 27px - closest to requested 25px */
  font-weight: ${fontWeights.semiBold}; /* 600 - exact match */
  color: ${colors.text.primary}; /* #403A60 - exact match */
  margin: 0;
  line-height: 1.2;
`;

// ─── News Page Components ────────────────────────────────────────────────────

export const NewsOuterContentContainer = styled(BaseOuterContentContainer)`
  min-height: 0;
`;

export const NewsContentContainer = styled(BaseContentContainer)`
  min-height: 0;
  overflow: hidden;
`;

export const NewsContentBody = styled(ContentBody)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const NewsSectionHeading = styled.h2`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[24]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  line-height: ${tokens.primitive.typography.lineHeight.relaxed};
  color: ${tokens.semantic.color.text.onLight};
  margin: ${tokens.primitive.space[20]} ${tokens.primitive.space[20]} 0;
  padding-bottom: ${tokens.primitive.space[10]};
  border-bottom: 1px solid ${tokens.primitive.color.grey300};
  flex-shrink: 0;
`;

export const NewsCardList = styled.div`
  ${mixins.flexColumn}
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: ${tokens.primitive.space[20]};

  > * + * {
    margin-top: ${tokens.primitive.space[15]};
  }
`;

export const NewsCard = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${tokens.primitive.color.offWhite};
  border-radius: ${tokens.primitive.radius[12]};
  padding: ${tokens.primitive.space[20]};
  flex-shrink: 0;

  > * + * {
    margin-inline-start: ${tokens.primitive.space[30]};
  }
`;

export const NewsCardThumbnail = styled.img`
  width: ${tokens.primitive.size.newsThumb.width};
  height: ${tokens.primitive.size.newsThumb.height};
  border-radius: ${tokens.primitive.radius[21]};
  object-fit: cover;
  flex-shrink: 0;
`;

export const NewsCardThumbnailPlaceholder = styled.div`
  width: ${tokens.primitive.size.newsThumb.width};
  height: ${tokens.primitive.size.newsThumb.height};
  flex-shrink: 0;
`;

export const NewsCardBody = styled.div`
  ${mixins.flexColumn}
  flex: 1;
  min-width: 0;
`;

export const NewsCardTitle = styled.h3`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: 1.4;
  color: ${tokens.semantic.color.text.onLight};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  padding-bottom: 2px;
`;

export const NewsCardDate = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.normal};
  color: ${tokens.semantic.color.text.dialogAccent};
  margin: 0;
  margin-top: ${tokens.primitive.space[6]};
`;

export const NewsCardDescription = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: 1.5;
  color: ${tokens.semantic.color.text.dialogAccent};
  margin: 0;
  margin-top: ${tokens.primitive.space[15]};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  padding-bottom: 2px;
`;

// Single-news fallback (no image, timestamp, or description)
export const NewsSingleText = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.relaxed};
  color: ${tokens.primitive.color.ink};
  margin: 0;
  padding: ${tokens.primitive.space[20]};
`;

// ─── News Source Tab Row (bottom) ────────────────────────────────────────────

export const NewsSourceList = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: ${tokens.primitive.space[16]} 0;

  > * + * {
    margin-inline-start: ${tokens.primitive.space[16]};
  }
`;

export const NewsSourceItem = styled.button`
  width: ${tokens.primitive.size.newsSourceCard.width};
  height: ${tokens.primitive.size.newsSourceCard.height};
  border-radius: ${tokens.primitive.radius[21]};
  background-color: ${tokens.primitive.color.purple};
  color: ${tokens.primitive.color.white};
  border: ${(props) => (props.active ? `6px solid ${tokens.primitive.color.gold}` : '0px')};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  padding: 0px;

  &:focus {
    outline: 6px solid ${tokens.primitive.color.gold};
    outline-offset: 2px;
  }

  img {
    background: ${tokens.semantic.color.background.screen};
    height: 100%;
    width: 100%;
    border-radius: ${(props) => (props.active ? tokens.primitive.radius[15] : tokens.primitive.radius[21])};
  }
`;


// =============================================================================
// SHARED HELPERS
// =============================================================================

const hiddenScrollbar = `
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const focusedBrightness = `
  filter: brightness(1.02);
`;


// =============================================================================
// ROOT TV CONTAINER
// =============================================================================

export const TvWrapper = styled.div`
  position: relative;
  direction: inherit;
  width: 100vw;
  height: 100vh;
   ${({ backgroundImage }) =>
    backgroundImage &&
    `
      background-image: url(${backgroundImage});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    `}
  background-color: ${tokens.primitive.color.black};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  color: ${tokens.primitive.color.white};
  overflow: hidden;
  user-select: none;
`;


// =============================================================================
// VIDEO BACKGROUND
// =============================================================================

export const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  background:${tokens.primitive.color.black};
  height: 100%;
  display: ${({ hide }) => (hide ? 'none' : 'block')};
  video, img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;


// DIRECTIONAL UTILITY LTR/RTL 
export const directionalStyles = (ltrStyles, rtlStyles) => `
  [dir='ltr'] & {
    ${ltrStyles}
  }

  [dir='rtl'] & {
    ${rtlStyles}
  }
`;

// =============================================================================
// TOP RIGHT CONTROLS
// =============================================================================

export const TopRightControls = styled.div`
  position: absolute;
  top: ${tokens.primitive.space[32]};
 
  ${directionalStyles(
  `right: ${tokens.primitive.space[48]};`,
  `left: ${tokens.primitive.space[48]};`
)}
  ${({ focused }) => focused && focusedBrightness}

  display: flex;
  gap: ${tokens.primitive.space[16]};
  z-index: 50;
`;

export const ControlPillButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${tokens.primitive.space[8]};

  background: ${tokens.primitive.color.glassy};
  border: ${tokens.primitive.space[1]} solid ${tokens.primitive.color.whiteAlpha10};
  
  border-radius: ${tokens.primitive.radius.full};

  padding: ${tokens.primitive.space[10]} ${tokens.primitive.space[20]};

  color: ${tokens.primitive.color.white};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[14]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  cursor: pointer;

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover,
  &:focus {
    transform: scale(1.04);
    background: ${tokens.primitive.color.primary700};
    border: ${tokens.primitive.space[2]} solid ${tokens.primitive.color.white};
    outline:none;
  }

  svg {
    width: ${tokens.primitive.size.icon.sm};
    height: ${tokens.primitive.size.icon.sm};
    fill: currentColor;
  }
`;


// =============================================================================
// CATEGORY SELECTOR CHIPS
// =============================================================================
export const CategoryChipList = styled.div`
  display: flex;
  gap: ${tokens.primitive.space[8]};
  overflow-x: auto;
  max-width: 720px;



  ${({ focused }) => focused && focusedBrightness}

  ${hiddenScrollbar}
`;

export const CategoryChipButton = styled.button`
  background: ${({ active }) =>
    active
      ? tokens.primitive.color.primary700 // equals SECONDARY_COLOR
      : tokens.semantic.color.background.glassy};

  border: ${({ active, highlighted }) =>
    highlighted
      ? `${tokens.primitive.space[2]} solid ${tokens.primitive.color.white}`
      : active
        ? `${tokens.primitive.space[2]} solid ${tokens.primitive.color.primary300}`
        : `${tokens.primitive.space[1]} solid ${tokens.primitive.color.whiteAlpha30}`};

  ${({ highlighted }) =>
    highlighted && 'transform: scale(1.01);'}

  border-radius: ${tokens.primitive.radius[12]};

  color: ${({ active }) =>
    active
      ? tokens.primitive.color.white
      : tokens.primitive.color.whiteAlpha70};

  padding: ${tokens.primitive.space[12]} ${tokens.primitive.space[20]};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[15]};
  font-weight: ${tokens.primitive.typography.fontWeight.bold};

  cursor: pointer;
  
  white-space: nowrap;


  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: ${tokens.primitive.shadow.white};
    border-color: ${tokens.primitive.color.white};
  }

  &:focus {
    outline: none;
    border-color: ${tokens.primitive.color.darkSecondary};  
  }
`;


// =============================================================================
// SIDEBAR OVERLAY
// =============================================================================


export const SidebarPanel = styled.div`
  position: absolute;
  top: 0;
 ${directionalStyles(
  `left: 0;`,
  `right: 0;`
)}
  width: ${tokens.primitive.size.tv.sidebarWidth};
  height: 100%;

 ${mixins.glass(tokens.primitive.color.black, 0.99)}
 
  padding: ${tokens.primitive.space[32]}
    ${tokens.primitive.space[24]};


  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: ${tokens.primitive.space[20]};

  z-index: 20;
`;

export const SidebarChannelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.primitive.space[10]};
  min-height:0;
  ${({ news_visible }) => news_visible && `margin-bottom:${tokens.primitive.space[35]};`}
  ${({ focused }) => focused && focusedBrightness}

  overflow-y: auto;
  flex: 1;
 
  margin-top:${tokens.primitive.space[10]};
  ${hiddenScrollbar}
`;

export const SidebarChannelRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${tokens.primitive.space[10]}
    ${tokens.primitive.space[16]};

    ${({ highlighted }) =>
    highlighted
      ? directionalStyles(`
              margin-left: ${tokens.primitive.space[10]};
              margin-right: ${tokens.primitive.space[2]};`, `
              margin-right: ${tokens.primitive.space[10]};
              margin-left: ${tokens.primitive.space[2]};`)
      : directionalStyles(
        `
              margin-left: ${tokens.primitive.space[2]};
              margin-right: ${tokens.primitive.space[10]};
            `,
        `
              margin-right: ${tokens.primitive.space[2]};
              margin-left: ${tokens.primitive.space[10]};
            `
      )};

  background: ${({ active }) =>
    active
      ? tokens.primitive.color.primary700
      :  tokens.semantic.color.background.glassyTransparent
        
  };

  border-radius: ${tokens.primitive.radius[14]};

  border: ${({ active, highlighted }) =>
    highlighted
      ? `${tokens.primitive.space[2]} solid ${tokens.primitive.color.white}`
      : active
        ? `${tokens.primitive.space[2]} solid ${tokens.primitive.color.primary300}`
        : `${tokens.primitive.space[1]} solid ${tokens.primitive.color.whiteAlpha10}`};

  cursor: pointer;

  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
`;

export const SidebarChannelLogoBox = styled.div`
  width: ${tokens.primitive.size.tv.sidebarLogo.width};
  height: ${tokens.primitive.size.tv.sidebarLogo.height};

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin: 0 ${tokens.primitive.space[12]};

  border-radius: ${tokens.primitive.radius[8]};
  overflow: hidden;

  background: ${tokens.primitive.color.grey500};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ChannelNumber = styled.span`
  width: ${tokens.primitive.size.tv.channelNumberWidth};

  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[14]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};

  color: ${tokens.primitive.color.white};
`;

export const ChannelTitle = styled.div`
  flex: 1;
  min-width: 0;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[15]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};

  color: ${tokens.primitive.color.white};
`;


// =============================================================================
// SIGNAL / SOUND WAVE
// =============================================================================

const soundWave = keyframes`
  0% {
    height: 30%;
  }

  50% {
    height: 100%;
  }

  100% {
    height: 30%;
  }
`;
const generateBarStyles = (count = 3) => {
  const styles = [];
  const baseDelays = [0.15, 0.05, 0.25, 0.1, 0.3, 0.2, 0.08];
  const baseHeights = [40, 70, 100, 50, 85, 60, 95];
  for (let i = 1; i <= count; i++) {
    const delay = baseDelays[(i - 1) % baseDelays.length];
    const height = baseHeights[(i - 1) % baseHeights.length];

    styles.push(`
      &:nth-child(${i}) {
        height: ${height}%;
        animation-delay: ${delay}s;
      }
    `);
  }
  return styles;
};
export const SignalIcon = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${tokens.primitive.space[2]};
  height: ${({ height }) => height || tokens.primitive.size.tv.signal.height};
  width: auto;
  margin:${tokens.primitive.space[10]} 0;
  span {
    width: ${tokens.primitive.size.tv.signal.barWidth};
    background-color: ${tokens.primitive.color.primary300};
    border-radius: ${tokens.primitive.radius[3]};
    animation: ${soundWave} ${({ barCount = 3 }) => Math.max(barCount * 0.025, 0.4)}s ease-in-out infinite alternate;

    /* Generates N child selectors dynamically */
    ${({ barCount = 3 }) => generateBarStyles(barCount)}
  }
`;


// =============================================================================
// STREAM LOADING OVERLAY
// =============================================================================

// Covers the video/player area (preview) or viewport (fullscreen) while a channel loads.
// Explicit top/left/right/bottom — `inset` is unsupported on Tizen 2.x–4.x / webOS 3.x.
export const StreamLoadingOverlay = styled.div`
  position: ${({ fullscreen }) => (fullscreen ? 'fixed' : 'absolute')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  z-index: ${({ fullscreen }) => (fullscreen ? 150 : 100)};
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  pointer-events: none;
`;

export const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;

  gap: ${tokens.primitive.space[14]};

  padding: ${tokens.primitive.space[12]}
    ${tokens.primitive.space[20]};

  border-radius: ${tokens.primitive.radius.full};

  background: ${tokens.primitive.color.tvLoadingBackground};


  border: ${tokens.primitive.space[1]} solid ${tokens.primitive.color.grey500};

  box-shadow: ${tokens.primitive.shadow.glassy};
`;

export const LoadingSpinner = styled.div`
  width: ${tokens.primitive.size.icon.md};
  height: ${tokens.primitive.size.icon.md};

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;
`;

export const LoadingSpinnerArc = styled.div`
  width: ${tokens.primitive.size.tv.spinner};
  height: ${tokens.primitive.size.tv.spinner};

  border-radius: 50%;

  border: ${tokens.primitive.space[2]} solid ${tokens.primitive.color.grey200};

  ${directionalStyles(
  `border-left-color: ${tokens.primitive.color.gold};`,
  `border-right-color: ${tokens.primitive.color.gold};`
)}

  animation: stream-spin 0.7s linear infinite;

  @keyframes stream-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.span`
  max-width: ${tokens.primitive.size.tv.loadingTextMaxWidth};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: ${tokens.primitive.color.white};

  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[15]};
  font-weight: ${tokens.primitive.typography.fontWeight.bold};

  letter-spacing:${tokens.primitive.space[2]};
`;


// ERROR STATES 
export const ErrorState = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: ${tokens.primitive.space[20]};
  box-sizing: border-box;

  text-align: center;
`;

export const ErrorStateText = styled.p`
  margin: 0;

  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  line-height: ${tokens.primitive.typography.lineHeight.relaxed};

  color: ${tokens.primitive.color.whiteAlpha70};
`;


export const NavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${tokens.primitive.space[10]};
`;

export const NavHeaderTitle = styled.span`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  color: ${tokens.semantic.color.text.onDark};
`;

export const BackIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${directionalStyles(
    `transform: rotate(0deg);`,
    `transform: rotate(180deg);`
  )}
`;





export const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path fill="none" stroke="white" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);

export const LogoDisabledIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path fill="none" stroke="white" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
    <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
export const ChevronLeftIcon = ({ width = 24, height = 24, color = `${tokens.primitive.color.white}`, ...props }) => {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" fill='none' stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
};


// Tizen-safe SVG Icons
export const TvIcon = ({height=32, width=32}) => (
  <svg  width={width} height={height} viewBox="0 0 24 24" fill='none' stroke={tokens.primitive.color.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="13" rx="2" ry="2" />
    <polyline points="17 2 12 7 7 2" />
  </svg>
);


export const RadioIcon = ({ width = 24, height = 24, variant = 'primary', ...props }) => {
  const stroke = {
    primary: tokens.primitive.color.purple,
    secondary: tokens.primitive.color.gold,
    outline: tokens.primitive.color.white,
  }[variant] || tokens.primitive.color.purple;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" stroke={stroke} />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5M19.1 4.9c3.9 3.9 3.9 10.3 0 14.2" />
    </svg>
  );
};

// NEWS TICKER DESIGN INTEGRATION 
export const SpeakerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.5 10.2L19 5V19L3.5 13.8V10.2ZM6.5 14L7 17.2C7.2 18.4 8.2 19.2 9.3 19.2C10.5 19.2 11.4 18.2 11.2 17L10.8 15.4"
      stroke="white"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// News Ticker Mixins
export const NewsTickerContainer = styled.div`
  width: 100vw;
  height: ${tokens.primitive.space[66]};
  display: flex;
  align-items: center;
  overflow: hidden;
  position: absolute;
  bottom:0;
  z-index:150;
  ${mixins.glass(tokens.primitive.color.black, 0.90)};
`;

export const NewsTickerIcon = styled.div`
  width: ${tokens.primitive.space[66]};
  min-width: ${tokens.primitive.space[66]};
  height: ${tokens.primitive.space[66]};
  display: flex;
  padding:${tokens.primitive.space[20]};
  align-items: center;
  justify-content: center;
  background: ${tokens.primitive.color.purple};
  position: relative;
  z-index: 2;
  ${directionalStyles(
  `transform: scaleX(1);`,
  `transform: scaleX(-1);`
)}
  box-shadow: ${tokens.primitive.space[12]} 0 ${tokens.primitive.space[20]} ${tokens.primitive.color.black} ;

`;

export const NewsTickerViewport = styled.div`
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
`;

export const NewsTickerTrack = styled.div`
  display: inline-flex;
  align-items: center;
  height: 100%;
  width: max-content;
  white-space: nowrap;
  animation-duration: ${({ items_count }) => `${Math.max(items_count * 10, 20)}s`};
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  ${directionalStyles(`
      flex-direction: row;
      animation-name: newsTickerScrollLTR;
    `,
  `flex-direction: row-reverse;
      animation-name: newsTickerScrollRTL;
    `
)}

  @keyframes newsTickerScrollLTR {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(-50%);
    }
  }

  @keyframes newsTickerScrollRTL {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(50%);
    }
  }
`;

export const NewsTickerItem = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onDark};
`;

export const NewsTickerText = styled.div`
  margin: 0 ${tokens.primitive.space[10]};
`;

export const NewsTickerSeparator = styled.div`
  width: 8px;
  height:8px;
  min-width: 8px;
  border-radius: 50%;
  margin:0px 10px;
  background: ${tokens.primitive.color.purple};
`;



// --Radio Redesign Styles--

export const RadioCardLogoBox = styled.div`
  width: ${tokens.primitive.space[44]};
  height: ${tokens.primitive.space[44]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: ${tokens.primitive.radius[12]};
  background-color: ${({ bgColor }) => bgColor || tokens.primitive.color.purple};
  ${directionalStyles(
    `margin-right: ${tokens.primitive.space[14]}`,`margin-left: ${tokens.primitive.space[14]}`
  )};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${tokens.primitive.radius[12]};
  }
`;

export const RadioCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;

export const RadioCardTitle = styled.span`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[19]};
  font-weight: ${tokens.primitive.typography.fontWeight.medium};
  color: ${tokens.primitive.color.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RadioCardFrequency = styled.span`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[14]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  color: ${tokens.primitive.color.grey500};
  letter-spacing: ${tokens.primitive.space[1]};
  margin-top: ${tokens.primitive.space[2]};
`;




// full screen radio player 
// --- Fullscreen Widget Styles ---
export const FullScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;

  background-color: ${tokens.primitive.color.black};

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  z-index: 2;
`;

/** Off-screen mount for device players in radio mode (audio-only streams). */
export const HiddenPlayerMount = styled.div.attrs(() => ({
  'data-tizen-video-area': 'true',
}))`
  position: fixed;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

export const AudioPlayerPreview = styled.div`
  width: 100vw;
  height: 100%;
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  ${directionalStyles(
    `border-left:${tokens.primitive.space[1]} solid ${tokens.primitive.color.whiteAlpha10}`,
    `border-right:${tokens.primitive.space[1]} solid ${tokens.primitive.color.whiteAlpha10}`
  )};
`;

export const StationCard = styled.div`
width: ${tokens.primitive.size.radio.radioCardWidth};
height: ${tokens.primitive.size.radio.radioCardHeight};
min-width: ${tokens.primitive.size.radio.radioCardWidth};
min-height: ${tokens.primitive.size.radio.radioCardHeight};

  background-color: ${({ bgColor }) => bgColor || tokens.primitive.color.purple};

  border-radius: ${tokens.primitive.radius[24] || '24px'};

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
`;

export const LiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.primitive.space[6]};

  margin-top: ${tokens.primitive.space[24]};
  margin-bottom: ${tokens.primitive.space[8]};

  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[12]};
  font-weight: ${tokens.primitive.typography.fontWeight.bold};

  color: ${tokens.primitive.color.white };

  letter-spacing: 1.5px;
  text-transform: uppercase;

  &::before {
    content: '';

    width: ${tokens.primitive.space[9]};
    height: ${tokens.primitive.space[9]};

    flex-shrink: 0;
    border-radius: 50%;

    background-color:${tokens.primitive.color.purple };
`;

export const StationTitle = styled.h1`
  font-family: ${tokens.primitive.typography.fontFamily.base};

  font-size: ${tokens.primitive.typography.fontSize[40] };
  font-weight: ${tokens.primitive.typography.fontWeight.regular };

  line-height: 1.2;

  color: ${tokens.primitive.color.white};

  margin: 0 0 ${tokens.primitive.space[4]} 0;

  text-align: center;
`;

export const StationFrequency = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};

  font-size: ${tokens.primitive.typography.fontSize[16] || '14px'};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};

  line-height: 1.2;

  color: ${tokens.primitive.color.grey500 || '#64748B'};

  letter-spacing: 1px;

  margin: 0 0 ${tokens.primitive.space[16]} 0;

  text-align: center;
`;


